/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */

import { useDisclosure, useToast, Box, Button } from '@chakra-ui/core';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useRouteMatch, useHistory } from 'react-router';
import isFunction from 'lodash/isFunction';
import { useCurrentUserProfile } from '../../../../app/hooks/useCurrentUserProfile';
import { PATH_HIRE_FORM_COPY, PATH_ADMINISTRATION_TEMPLATES } from '../../../../routes/constants';
import { generateFormSchema } from '../utils/generateFormSchema';
import { useStore, useStoreActions, useStoreState } from '../../../../models/hooks';
import LanguageReview from '../languageSelect/LanguageReview';
import useHandleTemplateValidation from '../errorHandling/validateTemplate';
import { isSectionSchema, populateHierarchyInfo } from '../utils';
import { initialDisclaimerText } from '../FinalSignature';

const SaveTemplate = React.memo(function SaveTemplateComponent() {
  const { t } = useTranslation('hrFormTemplate');
  const { onClose: onModalClose, isOpen: isModalOpen, onOpen: onModalOpen } = useDisclosure();
  const storeInstance = useStore();
  const toast = useToast();
  const {
    formSchema,
    currentLanguage,
    defaultLanguage,
    availableLanguages,
    templateName,
    needFinalSignature,
    validationErrors: errors,
    isUpdating: isApiUpdating,
  } = useStoreState((state) => state.formBuilder);
  const {
    setShowExitPrompt,
    updateTemplate,
    createTemplate,
    setError: setGlobalValidationError,
    setErrors: setGlobalValidationErrors,
  } = useStoreActions((state) => state.formBuilder);

  const { currentUserProfile } = useCurrentUserProfile();
  const params = useParams<{ templateId?: string }>();
  const templateIdFromUrl = params?.templateId;
  const matchCopyRoute = useRouteMatch(PATH_HIRE_FORM_COPY);
  const isDuplicate = !!matchCopyRoute;
  const accountId = currentUserProfile?.account ?? '';
  const history = useHistory();

  const { TemplateSchema } = useHandleTemplateValidation();

  const handleSaveTemplate = async () => {
    const trimmedTemplateName = templateName?.trim();
    if (!trimmedTemplateName) {
      setGlobalValidationError({
        message: t('validation.templateNameRequired') || 'Template name is required.',
        innerPath: 'name',
        fullPath: 'name',
      });
      toast({
        title: t('formBuilder.templateNameRequiredError') || 'Template name cannot be empty.',
        status: 'error',
        isClosable: true,
      });
      return;
    }

    try {
      const currentFormSchema = storeInstance.getState().formBuilder.formSchema;

      const newSchemaWithHierarchy = populateHierarchyInfo(currentFormSchema, null, null, null);

      const questionsToSave = generateFormSchema(newSchemaWithHierarchy, currentLanguage, false, availableLanguages);

      const validation = TemplateSchema.validate(
        { name: trimmedTemplateName, questions: questionsToSave, defaultLanguage, languages: availableLanguages },
        { abortEarly: false },
      );

      if (validation.error) {
        const errorWithQuestionID = validation.error.details.map((d) => {
          const root = d.context?.state?.ancestors?.[0]; // full object being validated

          return {
            ...d,
            questionId: String(root?.id),
          };
        });

        setGlobalValidationErrors(errorWithQuestionID);
        toast({
          description: t('formBuilder.validationErrorDescription') || 'Please correct the errors in the form.',
          status: 'error',
          isClosable: true,
        });
        return;
      }
      setGlobalValidationErrors([]);
      const disclaimerText = storeInstance.getState().formBuilder.disclaimerText || initialDisclaimerText;

      const payload = {
        name: trimmedTemplateName,
        questions: questionsToSave,
        needFinalSignature,
        defaultLanguage,
        languages: availableLanguages,
        signatureDetails: { disclaimerText: needFinalSignature ? disclaimerText : undefined },
      };
      setShowExitPrompt(false);
      if (templateIdFromUrl && !isDuplicate) {
        await updateTemplate({ account: accountId, id: templateIdFromUrl, payload });
      } else {
        /* eslint-disable */
        const response = await createTemplate({ account: accountId, payload });
        if (isDuplicate && response?.data?.templateId) {
          history.replace(PATH_ADMINISTRATION_TEMPLATES);
        } else if (!templateIdFromUrl && !isDuplicate) {
          history.replace(PATH_ADMINISTRATION_TEMPLATES);
        }
        /* eslint-enable */
      }

      toast({
        title: t('formBuilder.saveSuccessTitle', `Template ${templateIdFromUrl && !isDuplicate ? 'updated' : 'created'}`),
        status: 'success',
        isClosable: true,
      });

      setTimeout(() => {
        if (!(isDuplicate && templateIdFromUrl)) {
          history.replace(PATH_ADMINISTRATION_TEMPLATES);
        }
      }, 500);
    } catch (error) {
      /* eslint-disable  */
      const anyError = error as any;
      const message = (anyError?.response?.data?.errors?.[0]?.message ||
        anyError?.response?.data?.errors ||
        anyError?.message ||
        anyError?.errors ||
        t('formBuilder.saveErrorMessage')) as string;
      toast({
        description: message,
        status: 'error',
        isClosable: true,
      });
      /* eslint-enable  */
    }
  };

  const handleSaveButtonClick = useCallback(
    () => {
      const trimmedName = templateName?.trim();
      if (!trimmedName) {
        toast({
          title: t('formBuilder.templateNameRequiredError') || 'Template name cannot be empty.',
          status: 'warning',
          isClosable: true,
        });
        return;
      }

      if (availableLanguages.length > 1) {
        onModalOpen();
      } else {
        // eslint-disable-next-line
        handleSaveTemplate();
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [templateName, availableLanguages, handleSaveTemplate],
  );

  const handleSaveAttempt = async () => {
    const activeElement = document.activeElement as HTMLElement;
    // eslint-disable-next-line
    const isBlurFunction = isFunction(activeElement?.blur);
    if (activeElement && isBlurFunction && activeElement !== document.body) {
      activeElement.blur();
    }

    await new Promise((resolve) => setTimeout(resolve, 0));
    // eslint-disable-next-line
    handleSaveButtonClick();
  };

  const handleModalConfirmAndSave = useCallback(() => {
    onModalClose();
    // eslint-disable-next-line
    handleSaveTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onModalClose, handleSaveTemplate]);

  const isButtonDisabled = useMemo(() => {
    const hasErrors = errors && errors.length > 0;
    const isEmptySchema = formSchema.length === 0;
    const isSingleEmptySection =
      formSchema.length === 1 && isSectionSchema(formSchema[0]) && formSchema[0]?.fields?.length === 0 && !formSchema[0]?.section;

    const isSchemaEffectivelyEmpty = hasErrors || isEmptySchema || isSingleEmptySection;

    return isSchemaEffectivelyEmpty || isApiUpdating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSchema, isApiUpdating, errors]);

  return (
    <Box>
      <Button
        colorScheme="blue"
        isDisabled={isButtonDisabled}
        onMouseDown={(e) => e.preventDefault()}
        isLoading={isApiUpdating}
        onClick={handleSaveAttempt}
        data-testid="save-template-button"
        size="sm"
        variant="outline"
      >
        {t('formBuilder.save')}
      </Button>
      <LanguageReview
        isOpen={isModalOpen}
        onClose={onModalClose}
        onConfirm={handleModalConfirmAndSave}
        currentLanguage={currentLanguage}
        defaultLanguage={defaultLanguage}
        availableLanguages={availableLanguages}
      />
    </Box>
  );
});

export default SaveTemplate;
